import { CodeBlock } from '../../components/developers/CodeBlock';

export default function PolicyAuthoring() {
  return (
    <article className="prose prose-slate max-w-none">
      <h1>Policy Authoring</h1>
      <p className="lead">Learn how to write governance policies using the Opair policy DSL.</p>

      <h2>Policy Structure</h2>
      <p>Every policy consists of metadata, rules, and optional actions:</p>

      <CodeBlock
        title="basic-policy.opair"
        language="yaml"
        showLineNumbers
        code={`policy:
  name: content-moderation
  version: 1.0.0
  description: Content moderation policy for user-generated content

rules:
  - name: block-profanity
    when:
      input.content matches profanity_pattern
    then:
      decision: deny
      reason: "Content contains prohibited language"

  - name: limit-length
    when:
      length(input.content) > 10000
    then:
      decision: deny
      reason: "Content exceeds maximum length"

  - name: default-allow
    when: true
    then:
      decision: allow

actions:
  on_deny:
    - log_event(severity: "warning")
    - notify_moderator()`}
      />

      <h2>Rule Syntax</h2>

      <h3>Conditions</h3>
      <p>Use conditions to define when a rule should trigger:</p>

      <CodeBlock
        language="yaml"
        code={`# Comparison operators
when: input.score > 0.8
when: input.category == "sensitive"
when: input.count >= 10

# Logical operators
when: input.type == "comment" AND input.length > 100
when: input.source == "api" OR input.source == "webhook"
when: NOT input.verified

# Pattern matching
when: input.email matches ".*@company\\.com"
when: input.content contains "keyword"

# Collection operations
when: input.tags includes "urgent"
when: length(input.items) > 0
when: all(input.scores, x => x > 0.5)`}
      />

      <h3>Built-in Functions</h3>
      <table>
        <thead>
          <tr>
            <th>Function</th>
            <th>Description</th>
            <th>Example</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>length()</code>
            </td>
            <td>Get string or array length</td>
            <td>
              <code>length(input.text)</code>
            </td>
          </tr>
          <tr>
            <td>
              <code>lower()</code>
            </td>
            <td>Convert to lowercase</td>
            <td>
              <code>lower(input.name)</code>
            </td>
          </tr>
          <tr>
            <td>
              <code>upper()</code>
            </td>
            <td>Convert to uppercase</td>
            <td>
              <code>upper(input.code)</code>
            </td>
          </tr>
          <tr>
            <td>
              <code>trim()</code>
            </td>
            <td>Remove whitespace</td>
            <td>
              <code>trim(input.value)</code>
            </td>
          </tr>
          <tr>
            <td>
              <code>now()</code>
            </td>
            <td>Current timestamp</td>
            <td>
              <code>now()</code>
            </td>
          </tr>
          <tr>
            <td>
              <code>hash()</code>
            </td>
            <td>SHA-256 hash</td>
            <td>
              <code>hash(input.content)</code>
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Decision Types</h3>
      <CodeBlock
        language="yaml"
        code={`# Simple decision
then:
  decision: allow

# Decision with metadata
then:
  decision: deny
  reason: "Policy violation detected"
  metadata:
    rule_id: "r-001"
    severity: "high"

# Conditional decision
then:
  decision: allow
  conditions:
    - require_mfa: true
    - max_attempts: 3`}
      />

      <h2>Variables and Context</h2>
      <p>Define reusable variables and access context data:</p>

      <CodeBlock
        language="yaml"
        code={`variables:
  max_content_length: 10000
  allowed_domains:
    - "company.com"
    - "partner.org"
  rate_limits:
    free: 100
    pro: 1000

rules:
  - name: check-domain
    when:
      input.domain in variables.allowed_domains
    then:
      decision: allow

  - name: check-rate-limit
    when:
      context.request_count > variables.rate_limits[context.user.plan]
    then:
      decision: deny
      reason: "Rate limit exceeded"`}
      />

      <h2>Testing Policies</h2>
      <p>Write test cases to validate your policies:</p>

      <CodeBlock
        title="test-cases.yaml"
        language="yaml"
        code={`tests:
  - name: "Allow clean content"
    input:
      content: "Hello, this is a test message"
      userId: "user-123"
    expected:
      decision: allow

  - name: "Block profanity"
    input:
      content: "This contains bad words"
      userId: "user-456"
    expected:
      decision: deny
      reason: "Content contains prohibited language"

  - name: "Block oversized content"
    input:
      content: "x".repeat(15000)
      userId: "user-789"
    expected:
      decision: deny`}
      />

      <p>Run tests with the CLI:</p>
      <CodeBlock language="bash" code={`opair test ./policies --file test-cases.yaml --verbose`} />

      <h2>Best Practices</h2>

      <div className="not-prose my-8 p-4 bg-green-50 border border-green-200 rounded-lg">
        <h3 className="text-lg font-semibold text-green-900 mb-3">Policy Authoring Guidelines</h3>
        <ul className="text-green-800 space-y-2 text-sm">
          <li>
            <strong>Order matters:</strong> Rules are evaluated top-to-bottom; place specific rules
            before general ones
          </li>
          <li>
            <strong>Default rule:</strong> Always include a default allow/deny rule at the end
          </li>
          <li>
            <strong>Version policies:</strong> Use semantic versioning for policy updates
          </li>
          <li>
            <strong>Document rules:</strong> Add descriptions explaining the purpose of each rule
          </li>
          <li>
            <strong>Test thoroughly:</strong> Write test cases for edge cases and expected behavior
          </li>
          <li>
            <strong>Use variables:</strong> Extract magic numbers and repeated values into variables
          </li>
        </ul>
      </div>
    </article>
  );
}
